import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {environment} from "@environment";
import {AbstractComponent} from "../abstract.component";
import {CompetitorResultsService, LoaderService} from "@services";
import {
  CategoryItem,
  CategoryType,
  CompetitorFilter,
  CompetitorResults,
  CompetitorStatus,
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_SIZE,
  EventItem,
  Gender,
  Lookups,
  MeasurementTypes,
  PagedFilter,
  Pages,
  Paging,
  Race,
  RaceSettings,
  Request, ResultProcessingType,
  Waypoint,
  WaypointType
} from "@model";
import {DOCUMENT} from "@angular/common";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {ResultsDetailsModalComponent} from "../results-details-modal/results-details-modal.component";
import {TranslocoService} from "@ngneat/transloco";
import {debounceTime, distinctUntilChanged, filter, Subject} from 'rxjs';

@Component({
  selector: 'app-results-table',
  templateUrl: './results-table.component.html',
  styleUrls: ['./results-table.component.scss']
})
export class ResultsTableComponent extends AbstractComponent implements OnInit, Paging<CompetitorResults> {
  @Input() event: EventItem | undefined;
  @Input() race: Race | undefined;
  @Input() filteringEnabled: boolean = true;
  @Input() showEmptyState: boolean = true;
  @Input() showBorderBottom: boolean = true;
  @Input() results: CompetitorResults[] = [];
  @Input() categories: CategoryItem[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Input() genders: Lookups[] = [];
  @Input() isAdmin: boolean = false;
  @Input() isLoading: boolean = false;
  @Output() filterChange: EventEmitter<PagedFilter<CompetitorFilter>> = new EventEmitter<PagedFilter<CompetitorFilter>>();

  environment: any = environment;
  category: number | undefined = undefined;
  hasMore: boolean = false;
  showFabButton: boolean = false;
  page: number = DEFAULT_PAGE_INDEX;
  gender: number | undefined = undefined;
  filter: PagedFilter<CompetitorFilter> | undefined;
  raceId: number | undefined = undefined;
  measurementType: number | undefined = undefined;
  raceIds: number[] = [];
  raceNames: string[] = [];
  firstCompetitorResults: CompetitorResults | undefined = undefined;
  controlWaypoints: Waypoint[] = [];
  filterCategories: CategoryItem[] = [];
  filterCategoriesLabels: string[] = [];
  name: string = "";
  nameSearchInput$ = new Subject<string>();
  isRound: boolean = false;


  constructor(@Inject(DOCUMENT) private document: Document, private loaderService: LoaderService, private translocoService: TranslocoService,
              private competitorResultsService: CompetitorResultsService, private modalService: NgbModal) {
    super();
  }

  ngOnInit(): void {
    this.setFilterCategoriesAndLabels(this.categories);
    if (this.results.length > 0) {
      this.setFirstCompetitorResults(this.results[0]);
    }
    if (this.isAdmin) {
      this.measurementType = MeasurementTypes.GUN_TIME_PRIMARY;
    }
    this.raceId = this.race!.id;
    this.handleExploreResult(this.results);
    this.initRaceIds();
    this.initRaceNames();
    this.setCheckpointIds();
    this.initNameSearch();
    this.isRound = this.isRoundRace();
  }

  initNameSearch() {
    this.subscriptions.push(this.nameSearchInput$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        filter((value:string) => value.length === 0 || value.length >= 3)
      )
      .subscribe((value:string) => {
        this.name = value;
        this.onExplore(false);
      }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['results'] && !changes['results'].firstChange) {
      this.handleExploreResult(this.results);
    }
    if (changes['race'] && !changes['race'].firstChange) {
      this.setCheckpointIds();
      this.isRound = this.isRoundRace();
    }
  }

  setFilterCategoriesAndLabels(categories: CategoryItem[]) {
    const showCategories = !!this.race?.raceSettings ?
        !!this.race?.raceSettings?.category && this.race?.raceSettings?.category?.categoryType !== CategoryType.NOT_USED
        : categories.length;
    this.filterCategories = showCategories ? categories : [];
    this.filterCategoriesLabels = showCategories ? this.filterCategories.map((category: CategoryItem) => category.code ?? "-") : [];
  }

  private setFirstCompetitorResults(firstCompetitorResults: CompetitorResults | undefined) {
    this.firstCompetitorResults = firstCompetitorResults;
  }

  private setCheckpointIds() {
    if (!this.isRoundRace()) {
      const waypoints = this.race?.waypoints ?? [];

      this.controlWaypoints = waypoints
          .filter(item => item.waypointTypeId === WaypointType.CONTROL_WAYPOINT)
          .sort((a: Waypoint, b: Waypoint) => {
            const orderA = a.ordinalNumber ?? 0;
            const orderB = b.ordinalNumber ?? 0;
            return orderA - orderB;
          });
    } else {
      this.controlWaypoints = [];
    }
  }

  isRoundRace() {
    return Race.isBackyardUltra(this.race?.sport) || RaceSettings.isRoundRace(this.race?.raceSettings?.minimumLapDuration)
      || !!this.firstCompetitorResults?.competitorResultsDetail?.laps;
  }

  initRaceIds() {
    if (this.event?.racesList?.length ?? 0 > 0) {
      this.raceIds = this.event!.racesList!.map((race: Race) => race.id!);
    }
  }

  initRaceNames() {
    if (this.event?.racesList?.length ?? 0 > 0) {
      this.raceNames = this.event!.racesList!.map((race: Race) => race.name!);
    }
  }

  handleSearchInput(value: string): void {
    this.nameSearchInput$.next(value);
  }

  handleGenderSelection(value?: any): void {
    this.initFilterCategories(value);
    this.category = undefined;
    if (value === this.gender || !value) {
      this.gender = undefined;
    } else {
      this.gender = value;
    }
    this.name = "";
    this.onExplore(false);
  }

  initFilterCategories(value?: any) {
    if (value === Gender.MALE) {
      this.setFilterCategoriesAndLabels(this.categories.filter((category: CategoryItem) => category.gender === Gender.MALE));
    } else if (value === Gender.FEMALE) {
      this.setFilterCategoriesAndLabels(this.categories.filter((category: CategoryItem) => category.gender === Gender.FEMALE));
    } else {
      this.setFilterCategoriesAndLabels(this.categories);
    }
  }

  handleRaceSelection(value?: any): void {
    this.gender = undefined;
    this.category = undefined;
    this.race = this.event?.racesList?.find(item => item.id === value);
    this.raceId = value;
    this.competitorResultsService.findCategoryItemsByRaceId(this.race!.id!).subscribe(categories => {
      this.categories = categories;
      this.filterCategories = categories;
      this.setFilterCategoriesAndLabels(categories);
    });
    /// this is going to be checked on server for public/admin handling is different
    if (this.isAdmin) {
      this.measurementType = MeasurementTypes.GUN_TIME_PRIMARY;
    } else {
      this.measurementType = this.race?.raceSettings?.measurementType;
    }
    this.name = "";
    this.onExplore(false);
    this.setCheckpointIds();
    this.isRound = this.isRoundRace();
  }

  handleCategorySelection(value?: any): void {
    if (value === this.category || !value) {
      this.category = undefined;
    } else {
      this.category = value;
    }
    this.name = "";
    this.onExplore(false);
  }

  handleMeasurementTypeSelection(value?: any): void {
    if (value === this.measurementType || !value) {
      this.measurementType = undefined;
    } else {
      this.measurementType = value;
    }
    this.name = "";
    this.onExplore(false);
  }

  scrollToTop(): void {
    return this.document.body.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'start'
    });
  }

  private getFilter(): CompetitorFilter {
    let filter: CompetitorFilter = new CompetitorFilter();
    if (this.category) {
      filter.categories = [this.category];
    }
    filter.gender = this.gender ?? undefined;
    filter.name = this.name;
    filter.eventId = this.event?.id;
    filter.raceId = this.raceId;
    filter.measurementType = this.measurementType;
    return filter;
  }

  handleExploreResult(value: CompetitorResults[], loadMore?: boolean): void {
    if (value) {
      if (value.length > DEFAULT_PAGE_SIZE) {
        value = (value.slice(0, DEFAULT_PAGE_SIZE))
        this.hasMore = true;
      } else {
        this.hasMore = false;
      }
      if (loadMore && this.results) {
        value = this.results.concat(value)
      } else {
        if (this.name === "" && value.length > 0) {
          this.setFirstCompetitorResults(value[0]);
        }
      }
    }
    this.setListOfItems(value);
  }

  setListOfItems(value: Request[] | null) {
    if (value && value.length > 0) {
      this.results = value;
      this.page++;
    } else {
      this.results = [];
    }
  }

  onExplore(loadMore?: boolean) {
    if (loadMore === true) {
      this.loaderService.startLoading();
    } else {
      this.page = 1;
      this.isLoading = true;
    }
    this.filter = new PagedFilter(new Pages(undefined, this.page), this.getFilter());
    this.competitorResultsService.getCompetitorResults(this.filter, this.isAdmin).subscribe((value: CompetitorResults[]) => {
      if (loadMore === true) {
        this.loaderService.dismissLoading();
      } else {
        setTimeout(() => {
          this.isLoading = false;
        }, 300);
      }
      this.handleExploreResult(value, loadMore);
    }, (_) => {
      if (loadMore === true) {
        this.loaderService.dismissLoading();
      } else {
        this.isLoading = false;
      }
    });
    this.emitFilter();
  }

  emitFilter(): void {
    this.filterChange.emit(this.filter);
  }

  loadMore(): void {
    this.onExplore(true);
  }

  protected readonly Array: ArrayConstructor = Array;
  protected readonly MeasurementTypes = MeasurementTypes;

  openDetailsModal(competitorResults: CompetitorResults, isMobile: boolean = false): void {
    const modalRef = this.modalService.open(ResultsDetailsModalComponent, {
      centered: true,
      windowClass: isMobile ? 'results-details-modal modal-mobile' : 'results-details-modal',
    });
    modalRef.componentInstance.competitorResults = competitorResults;
    modalRef.componentInstance.firstCompetitorResults = this.firstCompetitorResults;
    modalRef.componentInstance.measurementType = this.measurementType;
    modalRef.componentInstance.category = this.category;
    modalRef.componentInstance.gender = this.gender;
    modalRef.componentInstance.isRoundRace = this.isRoundRace();
    modalRef.componentInstance.showDistance = this.race?.raceSettings?.resultProcessingType === ResultProcessingType.TIME_LIMITED;
    modalRef.componentInstance.race = this.race;
    modalRef.componentInstance.event = this.event;
    modalRef.componentInstance.languages = this.languages;
    modalRef.componentInstance.isAdmin = this.isAdmin;
  }

  scrollTable(isScrollRight: boolean): void {
    const table = document.querySelector('.table-responsive') as HTMLElement;
    if (table) {
      const scrollAmount = isScrollRight ? 200 : -200;
      table.scrollBy({left: scrollAmount, behavior: 'smooth'});
    }
  }

  protected readonly CompetitorStatus = CompetitorStatus;
  protected readonly ResultProcessingType = ResultProcessingType;
}
